import { useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";

import PageNav from "../components/common/pageNav.comp";
import OutlineBtn from "../components/form/outlineBtn.comp";
import Layout from "../components/layouts/layout.comp";
import OrderListComp from "../components/order/orderList.comp";
import OrderListFilterComp from "../components/order/orderListFilter.comp";
import Error from "../components/reuseables/error.comp";
import fileCore from "../core/file.core";
import orderCore from "../core/order.core";
import { useTranslation } from "../core/translation.core";
import useShop from "../hooks/useShop.hook";
import ErrorCenterIcon from "../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../icons/loadingOvalCenter.icon";
import { DeliveryStatus, OrderListFilter, OrderPayment, OrderStatus, PaymentStatus } from "../types/order";
import { ShipTypeOption } from "../types/shipSetting";
import ArrowDownTrayIcon from "../icons/arrowDownTray.icon";
import ModalWithBtn from "../components/reuseables/modalWithBtn.comp";
import ExcelIcon from "../icons/excel.icon";
import CsvIcon from "../icons/csv.icon";
import AddLink from "../components/form/addLink.comp";

interface TransactionExportRow {
  ["Order Number"]: string | undefined;
  ["Transaction Date"]: string;
  ["Order Amt"]: number;
  ["Amount"]: number;
  ["Payment Method"]: string;
  ["Balance"]: number;
}

export type OrderListColumnKey =
  | "orderNumber"
  | "date"
  | "orderStatus"
  | "paymentStatus"
  | "deliveryStatus"
  | "stockHoldStatus"
  | "customer"
  | "products"
  | "amount"
  | "paymentType"
  | "posSystem";

export type OrderCheckedItemsState = {
  [K in OrderListColumnKey]: boolean;
};

export const ORDER_REQUIRED_COLUMNS: OrderListColumnKey[] = ["orderNumber", "orderStatus", "paymentStatus", "amount"];
const ORDER_CHECKED_ITEMS_STORAGE_KEY = "orderList.checkedItems";

const DEFAULT_ORDER_CHECKED_ITEMS: OrderCheckedItemsState = {
  orderNumber: true,
  date: true,
  orderStatus: true,
  paymentStatus: true,
  deliveryStatus: true,
  stockHoldStatus: true,
  customer: true,
  products: true,
  amount: true,
  paymentType: true,
  posSystem: true,
};

export default function OrdersPage() {
  const { t, msg } = useTranslation();
  const { query, isReady } = useRouter();
  const methods = useForm<OrderListFilter>({
    defaultValues: {
      page: Number(query.page) || 1,
      pageSize: 50,
      dateOption: "last30Days",
      date: {
        from: format(addDays(new Date(), -30), "yyyy-MM-dd"),
        to: format(new Date(), "yyyy-MM-dd"),
      },
    },
  });

  useEffect(() => {
    const { unsubscribe } = methods.watch((data, { name, type }) => {
      if (name === "dateOption") {
        switch (data.dateOption) {
          case "today":
            setDate(new Date(), new Date());
            break;
          case "last7Days":
            setDate(addDays(new Date(), -7), new Date());
            break;
          case "last30Days":
            setDate(addDays(new Date(), -30), new Date());
            break;
          case "last365Days":
            setDate(addDays(new Date(), -365), new Date());
            break;
        }
      }

      if (name === "date.from" || name === "date.to") {
        const currentDateOption = methods.getValues("dateOption");
        const newDateOption = getDateOption(data.date?.from, data.date?.to);

        if (currentDateOption !== newDateOption) methods.setValue("dateOption", newDateOption);
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line
  }, [methods.watch]);

  function setDate(from: Date, to: Date) {
    methods.setValue("date", {
      from: format(from, "yyyy-MM-dd"),
      to: format(to, "yyyy-MM-dd"),
    });
  }

  function getDateOption(fromStr?: string, toStr?: string) {
    if (!fromStr || !toStr) return undefined;

    // const fromStr = getDateStr(from);
    // const toStr = getDateStr(to);

    if (fromStr === getDateStr(new Date()) && toStr === getDateStr(new Date())) return "today";
    if (fromStr === getDateStr(addDays(new Date(), -7)) && toStr === getDateStr(new Date())) return "last7Days";
    if (fromStr === getDateStr(addDays(new Date(), -30)) && toStr === getDateStr(new Date())) return "last30Days";
    if (fromStr === getDateStr(addDays(new Date(), -365)) && toStr === getDateStr(new Date())) return "last365Days";

    return undefined;
  }

  function getDateStr(date: Date) {
    return format(date, "yyyy-MM-dd");
  }

  return (
    <Layout
      title={t(msg.order.orders)}
      titleBtn={<AddLink key={1} id="order-create" href="/order/create" />}
      rightBtns={[<CreateTestOrdersBtn key={0} />, <DownloadBtn key={1} methods={methods} />]}
    >
      <Error />
      <Content methods={methods} />
    </Layout>
  );
}

function CreateTestOrdersBtn() {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  if (process.env.NODE_ENV !== "development") return null;

  return (
    <OutlineBtn
      id="test-orders"
      onClick={async () => {
        await orderCore.createTestOrders();
        queryClient.invalidateQueries({ queryKey: ["orders"] });
        location.reload();
      }}
    >
      {t(msg.order.createTestOrders)}
    </OutlineBtn>
  );
}

function Content({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const [checkedItems, setCheckedItems] = useState<OrderCheckedItemsState>(DEFAULT_ORDER_CHECKED_ITEMS);

  useEffect(() => {
    const savedPreferences = localStorage.getItem(ORDER_CHECKED_ITEMS_STORAGE_KEY);
    if (!savedPreferences) return;

    const parsedPreferences = JSON.parse(savedPreferences);
    const mergedPreferences: OrderCheckedItemsState = { ...DEFAULT_ORDER_CHECKED_ITEMS };

    for (const key of Object.keys(mergedPreferences) as OrderListColumnKey[]) {
      if (ORDER_REQUIRED_COLUMNS.includes(key)) {
        mergedPreferences[key] = true;
        continue;
      }
      mergedPreferences[key] = parsedPreferences[key] ?? mergedPreferences[key];
    }

    setCheckedItems(mergedPreferences);
  }, []);

  useEffect(() => {
    localStorage.setItem(ORDER_CHECKED_ITEMS_STORAGE_KEY, JSON.stringify(checkedItems));
  }, [checkedItems]);

  return (
    <>
      <OrderListFilterComp methods={methods} />
      <OrderColumnChooser checkedItems={checkedItems} setCheckedItems={setCheckedItems} />
      <OrderListAndPaging methods={methods} checkedItems={checkedItems} />
    </>
  );
}

function OrderListAndPaging({
  methods,
  checkedItems,
}: {
  methods: UseFormReturn<OrderListFilter, any>;
  checkedItems: OrderCheckedItemsState;
}) {
  const { query, isReady, replace } = useRouter();

  const [pageSize, orderStatuses, paymentStatuses, deliveryStatuses, date, customerId, productId, shipType, orderNumber] =
    useWatch({
      name: [
        "pageSize",
        "orderStatuses",
        "paymentStatuses",
        "deliveryStatuses",
        "date",
        "customerId",
        "productId",
        "shipType",
        "orderNumber",
      ],
      control: methods.control,
    });

  const isMounted = useRef(true);

  useEffect(() => {
    if (isReady && query.customerId) {
      methods.setValue("customerId", query.customerId as string);
    }

    if (isReady && query.from && query.to) {
      methods.setValue("date.from", query.from as string);
      methods.setValue("date.to", query.to as string);
    }
    // eslint-disable-next-line
  }, [isReady]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Reset to page 1 when filters change so we don't stay on page 10 with empty results
  useEffect(() => {
    if (!isMounted.current) return;

    const currentPage = Number(query.page) || 1;
    if (currentPage > 1) {
      replace({ pathname: "/orders", query: { ...query, page: 1 } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderStatuses, paymentStatuses, deliveryStatuses, date.from, date.to, customerId, productId, shipType, orderNumber]);

  const {
    data: orders,
    isError,
    isFetched,
    failureReason,
  } = useQuery({
    queryKey: [
      "orders",
      query.page,
      pageSize,
      orderStatuses,
      paymentStatuses,
      deliveryStatuses,
      date,
      customerId,
      productId,
      shipType,
      orderNumber,
    ],
    queryFn: (context) => {
      return orderCore.loadOrders({
        paging: {
          page: context.queryKey[1] as number,
          size: context.queryKey[2] as number,
        },
        orderStatuses: context.queryKey[3] as OrderStatus[],
        paymentStatuses: context.queryKey[4] as PaymentStatus[],
        deliveryStatuses: context.queryKey[5] as DeliveryStatus[],
        date,
        userIds: customerId ? [customerId] : undefined,
        productId: context.queryKey[8] as string,
        shipType: context.queryKey[9] as ShipTypeOption,
        orderNumber: context.queryKey[10] as string,
      });
    },
    enabled: isReady,
  });

  if (!isFetched) return <LoadingOvalCenterIcon />;
  if (isError) return <ErrorCenterIcon reason={failureReason} />;

  return (
    <>
      <OrderListComp orders={orders} checkedItems={checkedItems} />
      <Paging methods={methods} total={orders ? orders.size : 0} />
    </>
  );
}
function Paging({ methods, total }: { methods: UseFormReturn<OrderListFilter, any>; total: number }) {
  const router = useRouter();
  const [pageSize] = useWatch({
    name: ["pageSize"],
    control: methods.control,
  });

  return <PageNav page={Number(router.query.page) || 1} size={pageSize} total={total} pathname="/orders" query={router.query} />;
}

function DownloadBtn({ methods }: { methods: UseFormReturn<OrderListFilter, any> }) {
  const { selectedShop } = useShop();
  const { query } = useRouter();
  const [pageSize, orderStatuses, paymentStatuses, deliveryStatuses, date, customerId, productId, shipType] = useWatch({
    name: ["pageSize", "orderStatuses", "paymentStatuses", "deliveryStatuses", "date", "customerId", "productId", "shipType"],
    control: methods.control,
  });
  const [modalOpened, setModalOpened] = useState(false);
  const { t, msg } = useTranslation();

  // Helper function to calculate total paid amount from payments array
  function calculatePaidAmount(payments: any[] = []): number {
    return payments.reduce((total, payment) => {
      return total + (payment.amountReceived || 0);
    }, 0);
  }

  // Helper function to format payment methods string
  function formatPaymentMethods(payments: OrderPayment[] = []): string {
    if (!payments || payments.length === 0) {
      return "";
    }
    return payments
      .map((payment) => {
        const paymentName = payment.paymentName || "";
        const amount = payment.amountReceived || 0;
        return `${paymentName}:$${amount}`;
      })
      .join(", ");
  }

  async function handleDownload(downloadFormat: "csv" | "excel") {
    const payload: any[] = [];
    // orders are refetched here again because of size being set to a min of 10,000 records.
    const orders = await orderCore.loadOrders({
      paging: {
        page: Number(query.page),
        size: 10000,
      },
      orderStatuses,
      paymentStatuses,
      deliveryStatuses,
      date,
      userIds: customerId ? [customerId] : undefined,
      productId,
      shipType,
    });

    for (const order of orders.list) {
      const paidAmount = calculatePaidAmount(order.payments);
      const paymentMethods = formatPaymentMethods(order.payments);
      for (const product of order.products) {
        payload.push({
          ["Date And Time"]: order.orderAt,
          ["Retail Shop"]: order.retailShop?.name,
          ["Order Number"]: order.orderNumber,
          ["Staff"]: order.admin?.email,
          ["Customer Name"]:
            order.customer?.firstName && order.customer?.lastName
              ? `${order.customer.lastName} ${order.customer.firstName}`.trim()
              : order.customer?.firstName || order.customer?.lastName || "",
          ["Customer Email"]: order.customer?.email,
          ["Customer Phone Number"]: order.customer?.mobile,
          ["Product Sku"]: product.sku,
          ["Product Name"]: product.name,
          ["Unit Price"]: product.price,
          ["Unit Price Override"]: product.overridePrice,
          ["Quantity"]: product.quantity,
          ["Price"]: Number(product.overridePrice ?? product.price) * Number(product.quantity),
          ["Order Status"]: order.status,
          ["Payment Status"]: order.paymentStatus,
          ["Payment Method"]: paymentMethods,
          ["Pickup Status"]: order.pickupStatus,
          ["Paid Amt"]: paidAmount,
        });
      }
    }

    if (downloadFormat === "csv") {
      fileCore.downloadCSV(payload, `${selectedShop!.name.en}-[${format(new Date(), "yyyy-MM-dd")}]`);
      return;
    }

    const transactionsPayload: TransactionExportRow[] = [];
    for (const order of orders.list) {
      if (!order.payments || order.payments.length === 0) {
        continue;
      }

      const orderTotal = order.total || 0;
      const sortedPayments = [...order.payments].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });

      let cumulativePaid = 0;
      for (const payment of sortedPayments) {
        cumulativePaid += payment.amountReceived || 0;
        const balance = orderTotal - cumulativePaid;

        transactionsPayload.push({
          ["Order Number"]: order.orderNumber,
          ["Transaction Date"]: payment.createdAt ? format(new Date(payment.createdAt), "yyyy-MM-dd HH:mm:ss") : "",
          ["Order Amt"]: orderTotal,
          ["Amount"]: payment.amountReceived || 0,
          ["Payment Method"]: payment.paymentName || "",
          ["Balance"]: balance,
        });
      }
    }

    fileCore.downloadExcelMultiSheet(
      [
        { name: "Orders", data: payload },
        { name: "Transactions", data: transactionsPayload },
      ],
      `${selectedShop!.name.en}-[${format(new Date(), "yyyy-MM-dd")}].xlsx`
    );
  }

  return (
    <ModalWithBtn modalOpened={modalOpened} setModalOpened={setModalOpened} btnChild={<ArrowDownTrayIcon />}>
      <div className="py-10 flex flex-col items-center justify-center h-full gap-6">
        <h1 className="text-xl">{t(msg.order.downloadOrders)}</h1>
        <h2 className="text-md">{t(msg.order.selectDownloadFormat)}</h2>
        <div className="flex gap-10">
          <button
            id="order-list-download"
            onClick={() => {
              handleDownload("excel");
              setModalOpened(false);
            }}
          >
            <ExcelIcon className="h-20 w-20" />
          </button>
          <button
            id="order-list-download"
            onClick={() => {
              handleDownload("csv");
              setModalOpened(false);
            }}
          >
            <CsvIcon className="h-20 w-20" />
          </button>
        </div>
      </div>
    </ModalWithBtn>
  );
}

function OrderColumnChooser({
  checkedItems,
  setCheckedItems,
}: {
  checkedItems: OrderCheckedItemsState;
  setCheckedItems: React.Dispatch<React.SetStateAction<OrderCheckedItemsState>>;
}) {
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = e.target.name as OrderListColumnKey;
    if (ORDER_REQUIRED_COLUMNS.includes(key)) return;

    setCheckedItems((prev) => ({
      ...prev,
      [key]: e.target.checked,
    }));
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 w-full mb-2">
      {(Object.keys(checkedItems) as OrderListColumnKey[]).map((key) => {
        const isRequired = ORDER_REQUIRED_COLUMNS.includes(key);
        if (isRequired) return null;

        return (
          <label key={key} className="flex items-center cursor-pointer">
            <input type="checkbox" name={key} checked={checkedItems[key]} onChange={handleCheckboxChange} className="hidden" />
            <div
              className={`w-4 h-4 rounded border-2 ${
                checkedItems[key] ? "bg-blue-500 border-blue-500" : "border-gray-400"
              } flex items-center justify-center`}
            >
              {checkedItems[key] && (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7"></path>
                </svg>
              )}
            </div>
            <span className="ml-1 capitalize text-[12px]">
              <OrderColumnName name={key} />
            </span>
          </label>
        );
      })}
    </div>
  );
}

function OrderColumnName({ name }: { name: OrderListColumnKey }) {
  const { t, msg } = useTranslation();

  switch (name) {
    case "orderNumber":
      return <span>#</span>;
    case "date":
      return <span>{t(msg.order.date)}</span>;
    case "orderStatus":
      return <span>{t(msg.order.orderStatus)}</span>;
    case "paymentStatus":
      return <span>{t(msg.order.paymentStatus)}</span>;
    case "deliveryStatus":
      return <span>{t(msg.delivery.deliveryStatus)}</span>;
    case "stockHoldStatus":
      return <span>{t(msg.order.stockHoldStatus)}</span>;
    case "customer":
      return <span>{t(msg.order.customer)}</span>;
    case "products":
      return <span>{t(msg.product.products)}</span>;
    case "amount":
      return <span>{t(msg.order.amount)}</span>;
    case "paymentType":
      return <span>{t(msg.order.paymentType)}</span>;
    case "posSystem":
      return <span>{t(msg.pos.posSytem)}</span>;
    default:
      return <span>{name}</span>;
  }
}
